import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { normalize } from 'normalizr'

import { annualTaxFilingListSchema } from '../../../schema'
import {
  AnnualTaxFilingForm,
  TaxfyleJob,
  fetchAnnualTaxFilingFormsIfNeeded,
} from './annualTaxFilingForms.slice'
import { fetchIfNeededWrapper, fetchWrapper } from '@/reducers/fetch'
import { TaxFormType } from './questionnaires/constants'
import { TAX_ENTITY_TYPES } from '../taxConstants'
import { TAXFYLE_PORTAL_TRY_AGAIN_ERROR } from './constants'
import { adminApiAxios, apiAxios } from '@/api'

export interface AnnualTaxFilingStatus {
  id: number
  annualTaxFilingId: number
  annualTaxFilingStatusTypeId: number
  createdAt: string
  updatedAt: string
  AnnualTaxFilingStatusType: {
    name: string
  }
}

export type DependentRelationship =
  | 'son'
  | 'daughter'
  | 'mother'
  | 'father'
  | 'adopted_child'
  | 'foster_child'
  | 'grandchild'
  | 'other'
  | ''

export interface DependentsDetails {
  fullname: string
  dob: string
  relationship: DependentRelationship
}

export enum ConfirmAnnualTaxFiling {
  yes = 'yes',
  no = 'no',
  unsure = 'unsure',
}

export interface AnnualTaxFiling {
  id: number
  userId: number
  year: string
  expedited: boolean | null
  expeditedReason: string | null
  expeditedDueDate: string | null
  additionalTaxInformation: string | null
  priorYearTaxReturnUploaded: boolean | null
  surveyLastUpdatedAt: string | number | null
  needContractorFiling: needContractorFilingOption | null
  annualTaxFormNeeds: Array<TaxFormType> | null
  createdAt: string
  updatedAt: string
  expeditedAcknowledgeTimeConstraints: boolean | null
  expeditedAcknowledgeDocuments: boolean | null
  marriedFilingJointlyDetails: {
    fullname: string
    dob: string
  } | null
  dependentsDetails: Array<DependentsDetails> | null
  annualTaxFilingForms?: number[]
  googleSheetUrl: string | null
  pretaxSurveySubmittedAt: string | number | null
  optedOutAt: string | null
  taxOverviewCompletedAt: string | number | null
  endOfYearReviewCompletedAt: string | null
  confirmedFormNeeds: ConfirmAnnualTaxFiling | null
  isLateJoiner: boolean | null
  extensionTaxfyleJobId?: string | null
  isLateJoinerTermsAcceptedAt: string | null
  lateJoinerInitiallyOptedIn: boolean | null
  extensionRequestedAt: string | null
  extensionStartedAt: string | null
  extensionRequestSurveyCompletedAt: string | null
  extensionForced: boolean | null
  extensionReason: ExtensionReason | null
  extensionTaxfyleJob: TaxfyleJob | null
  exemptedFromExtensionAt: string | null
  exemptedFromExtensionReason: string | null
  taxNpsSubmittedAt: string | null
  taxSeasonSurveyStartedAt: string | null
  paidContractorThisYear: boolean | null
  taxSeasonSurveyInitialTaxEntity: TAX_ENTITY_TYPES | null
  taxSeasonSurveyNeedHelp: boolean | null
  taxSeasonSurveySelfDescribedTaxEntity: TAX_ENTITY_TYPES | null
  disqualifiedAt: string | null
  disqualifiedReason: AnnualTaxFilingDisqualifiedReason | null
}

// Mirror of the Prisma enum from api backend
export enum AnnualTaxFilingDisqualifiedReason {
  missed_tax_entity_verification_deadline = 'missed_tax_entity_verification_deadline',
  missed_bookkeeping_tasks_deadline = 'missed_bookkeeping_tasks_deadline',
  missed_books_locked_deadline = 'missed_books_locked_deadline',
  missed_tax_questionnaire_deadline = 'missed_tax_questionnaire_deadline',
  missed_1040_tax_return_extended_deadline = 'missed_1040_tax_return_extended_deadline',
  missed_1120s_tax_return_extended_deadline = 'missed_1120s_tax_return_extended_deadline',
}

export enum ExtensionRequestType {
  COMBINED = 'COMBINED',
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
  NONE = 'NONE',
}

export enum needContractorFilingOption {
  yes = 'yes',
  no = 'no',
  unsure = 'unsure',
}

export interface AnnualTaxFilingState {
  [key: string]: AnnualTaxFiling
}

export enum ExtensionReason {
  lateJoiner = 'LATE_JOINER',
  tskOverdue = 'TAX_SEASON_KICKOFF_OVERDUE',
  taxEntityUnconfirmed = 'TAX_ENTITY_UNCONFIRMED',
  bookkeepingTasksIncomplete = 'BOOKKEEPING_TASKS_INCOMPLETE',
  booksNotLocked = 'BOOKS_NOT_LOCKED',
  userRequested = 'USER_REQUESTED',
  taxQuestionnaireOverdue = 'TAX_QUESTIONNAIRE_OVERDUE',
  filedExternally = 'FILED_EXTERNALLY',
  filedWithRegularFiling = 'FILED_AS_PART_OF_RETURN_JOB',
}

const annualTaxFilingsSlice = createSlice({
  name: 'annualTaxFilings',
  initialState: {} as AnnualTaxFilingState,
  reducers: {
    getAllAnnualTaxFilings: (
      state,
      action: PayloadAction<AnnualTaxFilingState>
    ) => ({ ...state, ...action.payload }),
    getSingleAnnualTaxFiling: (
      state,
      action: PayloadAction<AnnualTaxFiling>
    ) => {
      state[action.payload.id] = action.payload
    },
  },
})

export default annualTaxFilingsSlice.reducer

// Actions
const { getAllAnnualTaxFilings, getSingleAnnualTaxFiling } =
  annualTaxFilingsSlice.actions

export const FETCH_TAX_FILINGS_KEY = 'FETCH_TAX_FILINGS_KEY'
export const fetchAnnualTaxFilingsIfNeeded = (forceFetch?: boolean) =>
  fetchIfNeededWrapper({
    fetchKey: FETCH_TAX_FILINGS_KEY,
    defaultErrorMessage: 'Error fetching all user tax filings',
    alwaysFetch: forceFetch,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<AnnualTaxFiling[]>(
        '/finances/api/v1/annual_tax_filings'
      )
      const normalizedData = normalize<
        AnnualTaxFiling,
        {
          annualTaxFilings?: { [key: string]: AnnualTaxFiling }
          annualTaxFilingForms?: { [key: string]: AnnualTaxFilingForm }
        },
        string[]
      >(json.data, annualTaxFilingListSchema)

      // Forms don't come back with all of the info we need so don't save it off to the redux.  Make the forms call
      // separately if needed
      dispatch(
        getAllAnnualTaxFilings(normalizedData.entities.annualTaxFilings || {})
      )

      return json.data
    },
  })

export const CREATE_ANNUAL_TAX_FILING_KEY = 'CREATE_ANNUAL_TAX_FILING_KEY'
export const createAnnualTaxFilings = (
  data: Partial<AnnualTaxFiling> & { year: string; message?: string }
) =>
  fetchWrapper({
    fetchKey: CREATE_ANNUAL_TAX_FILING_KEY,
    defaultErrorMessage: 'Error creating annual tax filing',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.post<AnnualTaxFiling>(
        '/finances/api/v1/annual_tax_filings',
        data
      )
      dispatch(getSingleAnnualTaxFiling(json.data))
      return json.data
    },
  })

export const UPDATE_ANNUAL_TAX_FILING_KEY = 'UPDATE_ANNUAL_TAX_FILING_KEY'
export const updateAnnualTaxFilings = (
  id: number,
  data: Partial<AnnualTaxFiling> & { message?: string }
) =>
  fetchWrapper({
    fetchKey: UPDATE_ANNUAL_TAX_FILING_KEY,
    defaultErrorMessage: 'Error updating tax filing',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<AnnualTaxFiling>(
        `/finances/api/v1/annual_tax_filings/${id}`,
        data
      )
      dispatch(getSingleAnnualTaxFiling(json.data))
      // The server adds and removes filing forms when form needs change.
      if ('annualTaxFormNeeds' in data) {
        await dispatch(fetchAnnualTaxFilingFormsIfNeeded(true))
      }
      return json.data
    },
  })

export const SUBMIT_ANNUAL_TAX_FILING_KEY = 'SUBMIT_ANNUAL_TAX_FILING_KEY'
export const submitAnnualTaxFiling = (
  id: number,
  data: Partial<AnnualTaxFiling>
) =>
  fetchWrapper({
    fetchKey: SUBMIT_ANNUAL_TAX_FILING_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<AnnualTaxFiling>(
        `/finances/api/v1/pre_tax_survey/${id}/submit`,
        data
      )
      dispatch(getSingleAnnualTaxFiling(json.data))
      return json.data
    },
  })

export const REQUEST_EXTENSION = 'REQUEST_EXTENSION'
export const requestExtension = (extensionReason: ExtensionReason) =>
  fetchWrapper({
    fetchKey: REQUEST_EXTENSION,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.put<AnnualTaxFiling>(
        '/finances/api/v1/annual_tax_filings/request_extension',
        { reason: extensionReason }
      )
      dispatch(getSingleAnnualTaxFiling(json.data))
      return json.data
    },
  })

export const REQUEST_1099_MAGIC_LINK_KEY = 'REQUEST_1099_MAGIC_LINK_KEY'
export const get1099NecMagicLink = () =>
  fetchWrapper({
    fetchKey: REQUEST_1099_MAGIC_LINK_KEY,
    defaultErrorMessage: TAXFYLE_PORTAL_TRY_AGAIN_ERROR,
    fetchFunction: async () => {
      const json = await apiAxios.get<{ url: string | null }>(
        '/finances/api/v2/contractor_filings/job/url'
      )
      return json.data
    },
  })

export const REQUEST_ADMIN_CONTRACTOR_FILING_JOB_ID_KEY = (userId: number) =>
  `${userId}-REQUEST_ADMIN_CONTRACTOR_FILING_JOB_ID_KEY`
export const getAdminContractorFilingJobId = (userId: number) =>
  fetchWrapper({
    fetchKey: REQUEST_ADMIN_CONTRACTOR_FILING_JOB_ID_KEY(userId),
    fetchFunction: async () => {
      const json = await adminApiAxios.get<{ jobId: string | null }>(
        `/finances/api/v2/admin/contractor_filings/${userId}/job/id`
      )
      return json.data
    },
  })

export const REQUEST_ADMIN_CONTRACTOR_FILING_JOB_URL_KEY = (userId: number) =>
  `${userId}-REQUEST_ADMIN_CONTRACTOR_FILING_JOB_URL_KEY`
export const getAdminContractorFilingJobUrl = (
  userId: number,
  taxYear: string
) =>
  fetchWrapper({
    fetchKey: REQUEST_ADMIN_CONTRACTOR_FILING_JOB_URL_KEY(userId),
    fetchFunction: async () => {
      const json = await adminApiAxios.get<{ url: string | null }>(
        `/finances/api/v2/admin/contractor_filings/${userId}/job/url`,
        { params: { taxYear } }
      )
      return json.data
    },
  })

export const REQUEST_CAN_OPT_OUT_OF_1099_NEC_KEY =
  'REQUEST_CAN_OPT_OUT_OF_1099_NEC_KEY'
export const getCanOptOutOf1099Nec = () =>
  fetchWrapper({
    fetchKey: REQUEST_CAN_OPT_OUT_OF_1099_NEC_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.get<{ ableToOptOut: boolean }>(
        '/finances/api/v2/contractor_filings/able_to_opt_out'
      )

      return json.data
    },
  })

export const START_CONTRACTOR_FILING_JOB_KEY = 'START_CONTRACTOR_FILING_JOB_KEY'
export const startContractorFilingJob = () =>
  fetchWrapper({
    fetchKey: START_CONTRACTOR_FILING_JOB_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.post<{ job: TaxfyleJob }>(
        '/finances/api/v2/contractor_filings/job'
      )
      return json.data
    },
  })

export interface ContractorFilingPrefill {
  businessName: string | null
  businessNameIsLegalName: boolean
  contractorStates: string[]
  hasPayrollProvider: true | null
}

export const GET_CONTRACTOR_FILING_QUESTIONNAIRE_KEY =
  'GET_CONTRACTOR_FILING_QUESTIONNAIRE_KEY'
export const getContractorFilingQuestionnaire = () =>
  fetchWrapper({
    fetchKey: GET_CONTRACTOR_FILING_QUESTIONNAIRE_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.get<{ prefill: ContractorFilingPrefill }>(
        '/finances/api/v2/contractor_filings/questionnaire'
      )
      return json.data
    },
  })

export interface ContractorFilingAnswers {
  businessName: string
  hasPayrollProvider: boolean
  contractorCount: number
  contractorStates: string[]
}

export const SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY =
  'SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY'
export const submitContractorFilingQuestionnaire = (
  answers: ContractorFilingAnswers
) =>
  fetchWrapper({
    fetchKey: SUBMIT_CONTRACTOR_FILING_QUESTIONNAIRE_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.post<{ job: TaxfyleJob }>(
        '/finances/api/v2/contractor_filings/questionnaire',
        answers
      )
      return json.data
    },
  })

export const GET_CONTRACTOR_FILING_JOB_KEY = 'GET_CONTRACTOR_FILING_JOB_KEY'
export const getContractorFilingJob = () =>
  fetchWrapper({
    fetchKey: GET_CONTRACTOR_FILING_JOB_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.get<{ job: TaxfyleJob | null }>(
        '/finances/api/v2/contractor_filings'
      )
      return json.data
    },
  })

export interface AnnualTaxFilingNonNormalized extends Omit<
  AnnualTaxFiling,
  'annualTaxFilingForms'
> {
  annualTaxFilingForms: AnnualTaxFilingForm[]
}

export interface TaxFilingUser {
  id: number
  uuid: string
  firstName: string
  lastName: string
  email: string
  createdAt: string
  annualTaxFilings?: AnnualTaxFilingNonNormalized[]
  bookkeeperId: number
  bookkeeper: {
    firstName: string
    lastName: string
  }
  ownerId: number
  owner: {
    firstName: string
    lastName: string
  }
  taxPreparerId: number
  taxPreparer: {
    firstName: string
    lastName: string
  }
  taxCoordinatorId: number
  taxCoordinator: {
    firstName: string
    lastName: string
  }
}
